import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';

import { AccountService, AlertService } from '@app/_services';
import { MustMatch } from '@app/_helpers';
import { PatientService } from '@app/_services/patient.service';
import { EmailVerificationService } from '@app/_services/email-verification.service';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Component({
  templateUrl: 'register.component.html',
  styleUrls: ['./register.style.scss'],
})
export class RegisterComponent implements OnInit {
  form: FormGroup;
  loading = false;
  submitted = false;
  isOtherSelected = false;

  isCodeSent = false;
  isVerfied = false;
  message: string = '';
  lastSentTime: number | null = null;
  errorMessage;
  savedLang;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private alertService: AlertService,
    private patientService: PatientService,
    private emailService: EmailVerificationService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.form = this.formBuilder.group(
      {
        //title: ['', Validators.required],
        first_name: ['', Validators.required],
        last_name: ['', Validators.required],
        dob: ['', Validators.required],
        //height: ['', Validators.required],
        //weight: ['', Validators.required],
        sex: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        code: ['', [Validators.required, Validators.minLength(6)]],
        mobile: ['', [Validators.required, Validators.minLength(10)]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            this.passwordComplexityValidator,
          ],
        ],
        confirmPassword: ['', Validators.required],
        referralSource: ['', Validators.required],
        otherDetails: [''],
        acceptTerms: [false, Validators.requiredTrue],
      },
      {
        validator: MustMatch('password', 'confirmPassword'),
      }
    );
  }

  passwordComplexityValidator(control: any) {
    const password = control.value || '';
    const hasNumber = /\d/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>+\-_‘’[\]\\\/`~]/.test(
      password
    );

    const isValid =
      hasNumber && hasUpperCase && hasLowerCase && hasSpecialCharacter;

    return isValid ? null : { passwordComplexity: true };
  }

  updateMessage(key: string) {
    this.translate.get(key).subscribe((res: string) => {
      this.message = res;
    });
    this.cdr.detectChanges();
  }

  sendCode() {
    const currentTime = new Date().getTime(); // Get the current timestamp (in milliseconds)
    const email = this.form.get('email')?.value;

    if (!email) {
      this.updateMessage('REGISTER_MSG_VALID_EMAIL');
      // this.message = 'Please enter a valid email address.';
      return;
    }

    if (this.lastSentTime && currentTime - this.lastSentTime < 10 * 60 * 1000) {
      // If the time interval from the last sent code is less than 10 minutes
      this.updateMessage('REGISTER_MSG_CODE_NOT_EXPIRED');
      // this.message =
      //   'It has been less than 10 minutes since the last verification<br/>code was sent. Please try again later.';
      return;
    }

    this.emailService.checkEmail(email).subscribe({
      next: (res) => {
        if (res.checkFlg) {
          this.updateMessage('REGISTER_MSG_EMAIL_EXIST');
          // this.message = 'Email already exists. You can log in directly.';
        } else {
          this.emailService.sendCode(email).subscribe({
            next: () => {
              this.isCodeSent = true;
              // alert('Verification code sent to your email!');
              this.lastSentTime = currentTime; // Update the last sent time
              this.updateMessage('REGISTER_MSG_CODE_SEND');
              // this.message =
              //   'The verification code has been sent to your registered email. <br/>Please check your inbox for the code.';
            },
            error: (err) => {
              this.message = `Error sending code: ${
                err.error.detail || 'Unexpected error occurred.'
              }`;
              // alert(`Error:${err.error.error}`);
            },
          });
        }
      },

      // if (err.error?.error?.detail) {
      //   this.message = err.error.error.detail;
      // } else {
      //   this.message = 'An unexpected error occurred while checking email.';
      // }
      // if (err.status === 400 && err.error.detail) {
      //   this.message = err.error.detail;
      // } else {
      //   this.message = 'An unexpected error occurred while checking email.';
      // }
      // },
    });
  }

  // verfyCode() {
  // const email = this.form.get('email')?.value;
  // this.emailService.verifyCode(this.email, this.code).subscribe({
  //   next: () => {
  //     this.isVerfied = true;
  //     alert('Email verified successfully!');
  //   },
  //   error: (err) => {
  //     alert(`Error:${err.error.error}`);
  //   },
  // });
  // }

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  onReferralSourceChange(): void {
    const selectedSource = this.form.get('referralSource')?.value;
    this.isOtherSelected = selectedSource === 'other';

    if (!this.isOtherSelected) {
      this.form.get('otherDetails')?.setValue('');
    }
  }

  onSubmit() {
    this.submitted = true;

    // reset alerts on submit
    this.alertService.clear();

    // stop here if form is invalid
    if (this.form.invalid) {
      return;
    }

    const email = this.form.get('email')?.value;
    const code = this.form.get('code').value;
    this.emailService.verifyCode(email, code).subscribe({
      next: () => {
        // alert('Email verified successfully! Proceeding with registration...');
        // this.completeRegistration();
        this.loading = true;
        // this.patientService.savePatientData(this.form.value)
        this.accountService
          .register(this.form.value)
          .pipe(first())
          .subscribe({
            next: () => {
              // this.alertService.success(
              //   'Registration successful, please check your email for verification instructions',
              //   { keepAfterRouteChange: true }
              // );
              this.translate.get('REGISTER_MSG_OK').subscribe((res: string) => {
                this.alertService.success(res, { keepAfterRouteChange: true });
              });

              this.router.navigate(['../login'], { relativeTo: this.route });
            },
            error: (error) => {
              // this.alertService.error(error);
              this.loading = false;
              if (error.status === 400 && error.error.detail) {
                // this.errorMessage = error.error.detail;
                this.alertService.error(error.error.detail);
              } else {
                // this.errorMessage = 'An unexpected error occurred.';
                this.alertService.error('An unexpected error occurred.');
              }
            },
          });
      },
      error: (err) => {
        this.alertService.error(`Error: ${err.error.error}`);
      },
    });
  }
}
